import { Symbols } from '@mnd-frontend/ui/publish/components';
import {
  GREY_100,
  GREY_200,
  GREY_400,
  GREY_800,
  RED_500,
  RED_700,
} from '@mnd-frontend/ui/shared/tokens';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import type { UploadingStates } from './constants';
import IconAndCaption from './IconAndCaption';

const idleStyle = css`
  transition: background-color 0.2s ease-out;
  svg {
    .cloud-border {
      fill: ${GREY_800};
    }
    .cloud {
      fill: ${GREY_400};
    }
    .arrow {
      fill: ${GREY_800};
      transition:
        transform 0.2s ease-out,
        fill 0.2s ease-out;
      transform: translateY(0rem);
    }
    path {
      transition: fill 0.2s ease-out;
    }
  }
  &:hover {
    background-color: ${GREY_200};
    transition: background-color 0s ease-out;
  }
  &:hover:active {
    background-color: ${GREY_100};
  }
  &:hover {
    svg {
      path {
        transition: fill 0.2s ease-out;
        fill: ${RED_700};
      }
      .cloud {
        fill: ${RED_500};
      }
      .arrow {
        transition:
          transform 0.2s ease-out,
          fill 0.2s ease-out;
        transform: translateY(-0.12rem);
      }
    }
  }
  &:hover:active {
    svg {
      transform: scale(0.98);
      path {
        fill: ${RED_700};
      }
      .cloud {
        fill: ${RED_500};
      }
      .arrow {
        transition: transform 0s ease-out;
        transform: translateY(-0.15rem);
      }
    }
  }
`;

const loadingStyle = css`
  @keyframes arrow {
    0% {
      transform: translateY(0rem);
      opacity: 1;
    }
    35% {
      opacity: 1;
    }
    50% {
      transform: translateY(-0.3rem);
      opacity: 0;
    }
    89% {
      transform: translateY(0.1rem);
      opacity: 0;
    }
    100% {
      opacity: 1;
      transform: translateY(0rem);
    }
  }
  svg {
    path {
      fill: ${GREY_800};
    }
    .cloud {
      transform: translateY(0rem);
      fill: ${GREY_400};
    }
    .arrow {
      animation: arrow 1.5s ease-out infinite backwards;
    }
  }
`;

const Wrapper = styled.div<{ $idle: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;

  svg[data-icon='cloud-upload'] {
    display: block;
    width: 3.125rem;
    height: 3.125rem;
  }

  ${({ $idle }) => ($idle ? idleStyle : loadingStyle)}
`;

const StyledInput = styled.input`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const FileInputField = ({
  inputRef,
  status,
  loading,
  caption,
  loadingCaption,
  disabled,
  multiple,
  acceptFormats,
  onChange,
  icon = <Symbols.UploadNoWrapper />,
  smallCaption = false,
}: {
  inputRef?: React.RefObject<HTMLInputElement>;
  status: UploadingStates;
  loading?: boolean;
  caption: string;
  loadingCaption?: string;
  disabled?: boolean;
  multiple?: boolean;
  acceptFormats?: string[];
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  icon?: React.ReactNode;
  smallCaption?: boolean;
}) => {
  const { t } = useTranslation();
  const loadingState = status === 'LOADING' || loading;

  return (
    <Wrapper $idle={!loadingState}>
      {loadingState ? (
        <IconAndCaption
          disabled={true}
          icon={icon}
          smallCaption={smallCaption}
          caption={loadingCaption || t('components_image-upload_uploading')}
        />
      ) : (
        <>
          <IconAndCaption
            disabled={disabled}
            icon={icon}
            caption={caption}
            smallCaption={smallCaption}
          />
          <StyledInput
            ref={inputRef}
            type="file"
            disabled={disabled}
            onChange={onChange}
            multiple={multiple}
            accept={acceptFormats ? acceptFormats.join(', ') : undefined}
            aria-label="file-input-field"
            data-is-file-uploader
          />
        </>
      )}
    </Wrapper>
  );
};

export default FileInputField;
